import type { InstanceMember } from "@keydials/api-client";

/**
 * A User attribute (KEY-267) stores a bare user id and means "one of this dial's members". Everything the
 * field needs to render is already on the instance detail — `members` is loaded on every fetch — so resolving
 * a value costs no extra call, and the picker offers exactly the people the server will accept.
 */

/** The name to show for a member: what they chose to be called, falling back to the address we know. */
export function memberLabel(member: InstanceMember): string {
  return member.displayName?.trim() || member.email;
}

/** Whole-dial grant first, then section, then segment — the broadest grant describes the person's reach. */
function byBreadth(a: InstanceMember, b: InstanceMember): number {
  return (a.sectionKey ?? "").localeCompare(b.sectionKey ?? "")
    || (a.segmentKey ?? "").localeCompare(b.segmentKey ?? "");
}

/**
 * One row per person on the dial, folded down from the several grants one user may hold here (whole dial,
 * a section, a segment) — the same grouping the admin members panel does, kept local to this package.
 *
 * The grant kept for each person is their broadest, so the role shown next to a name describes their reach
 * on the dial rather than whichever grant happened to come back first. Archived accounts are left out:
 * sign-in is blocked for them, so offering one to assign work to is a dead end. A value that already names
 * an archived member still resolves — see {@link userAttributeState} — because hiding a name we hold would
 * be worse than showing a person who has since left.
 */
export function distinctInstanceMembers(members: InstanceMember[]): InstanceMember[] {
  const byUser = new Map<string, InstanceMember>();
  for (const m of members) {
    if (m.archived) continue;
    const held = byUser.get(m.userId);
    if (!held || byBreadth(m, held) < 0) byUser.set(m.userId, m);
  }
  return [...byUser.values()].sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)));
}

export interface UserAttributeState {
  /** The user id the value names; null when the field is unset. */
  userId: string | null;
  /** The member that id resolves to — including an archived one, so a held name is never dropped. */
  member: InstanceMember | null;
  /**
   * A value is set but names nobody on this dial. Almost always a member whose grant has since been
   * revoked: revoking access deliberately leaves assignments in place (as it does for every other authored
   * record), so the field keeps its answer and the apps flag it rather than blanking it.
   */
  unresolved: boolean;
  /** The people the picker offers, one row each, in display order. */
  options: InstanceMember[];
}

/** Resolves what a User field should show and offer, against the members already on the instance detail. */
export function userAttributeState(value: string, members: InstanceMember[]): UserAttributeState {
  const userId = value.trim() || null;
  // Ids come from the server in one canonical spelling, but a value could predate that or arrive by import;
  // compare case-insensitively so a differently-cased id resolves instead of reading as a lost member.
  const member = userId
    ? members.find((m) => m.userId.toLowerCase() === userId.toLowerCase()) ?? null
    : null;

  return {
    userId,
    member,
    unresolved: userId !== null && member === null,
    options: distinctInstanceMembers(members),
  };
}
